<template>
  <Section id="solutions" tone="default" pad-y="lg" aria-labelledby="solutions-heading">
    <Container>
      <div class="mb-10 flex flex-col gap-3">
        <h2 id="solutions-heading" class="text-3xl font-bold text-ink-strong md:text-4xl">
          {{ t('sections.solutions.title') }}
        </h2>
        <p class="max-w-2xl text-base text-ink-muted md:text-lg">
          {{ t('sections.solutions.subtitle') }}
        </p>
      </div>

      <Grid :cols="{ base: 1, md: 2, lg: 3 }" :gap="6">
        <SolutionCard
          v-for="item in visible"
          :key="item.id"
          :title="solutionTitle(item)"
          :description="solutionDescription(item)"
          :details="solutionDetails(item)"
          :cta-label="t('cta.details')"
          @open="clickCardButton(item)"
        />
      </Grid>
      <SolutionCard
        class="mt-6"
        :title="t('sections.solutions.cardNotFound.title')"
        :description="t('sections.solutions.cardNotFound.subtitle')"
        :cta-label="t('cta.details')"
        @open="scrollToForm"
      />

      <div class="mt-8 flex flex-wrap items-center justify-center gap-4 md:hidden">
        <BaseButton v-if="canCollapse" variant="secondary" @click="expanded = !expanded">
          {{ expanded ? t('cta.showLess') : t('cta.showAll') }}
        </BaseButton>
      </div>

      <!--<div class="mt-10 flex justify-center">
        <BaseButton variant="primary" size="lg" as="router-link" :to="calculatorTo">
          {{ t('cta.calcEstimate') }}
        </BaseButton>
      </div>-->
    </Container>

    <Modal
      :open="activeSolution !== null"
      :title="activeSolution ? solutionTitle(activeSolution) : ''"
      size="md"
      @update:open="(value) => !value && closeModal()"
    >
      <div v-if="activeSolution" class="flex flex-col gap-3 text-sm text-ink md:text-base">
        <p>{{ solutionDescription(activeSolution) }}</p>
        <ul
          v-if="solutionDetails(activeSolution).length > 0"
          class="flex flex-col gap-1.5 text-sm text-ink"
        >
          <li v-for="d in solutionDetails(activeSolution)" :key="d" class="flex items-start gap-2">
            <span class="text-accent">•</span>
            <span>{{ d }}</span>
          </li>
        </ul>
      </div>
    </Modal>
  </Section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Section from '@/components/primitives/Section.vue';
import Container from '@/components/primitives/Container.vue';
import Grid from '@/components/primitives/Grid.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import Modal from '@/components/feedback/Modal.vue';
import SolutionCard from '@/components/domain/SolutionCard.vue';
import { READY_SOLUTIONS, type SolutionItem } from '@/content/home-data';
import { useLocale } from '@/composables/useLocale';
import { pickLocalized, pickLocalizedArray } from '@/lib/i18n/localize';
import { useReducedMotion } from '@/composables/useReducedMotion';
import { useRouter } from 'vue-router';
import { yaTrackEvent } from '@/lib/metrics';

const { t } = useI18n();
const { locale } = useLocale();
const reduced = useReducedMotion();
const router = useRouter();

function solutionTitle(item: SolutionItem): string {
  return pickLocalized(item, 'title', locale.value);
}

function solutionDescription(item: SolutionItem): string {
  return pickLocalized(item, 'description', locale.value);
}

function solutionDetails(item: SolutionItem): readonly string[] {
  return pickLocalizedArray(item, 'details', locale.value);
}

function solutionTo(item: SolutionItem): string | null {
  if (!item.subproductId) return null;
  return `/${locale.value}/solutions/${item.subproductId}`;
}

const expanded = ref(false);
const activeSolution = ref<SolutionItem | null>(null);

const visible = computed(() => (expanded.value ? READY_SOLUTIONS : READY_SOLUTIONS.slice(0, 6)));
const canCollapse = computed(() => READY_SOLUTIONS.length > 6);

/*function openModal(item: SolutionItem): void {
  activeSolution.value = item;
}*/

function closeModal(): void {
  activeSolution.value = null;
}

function scrollToForm(): void {
  const target = document.getElementById('contact');
  if (!target) return;
  const rect = target.getBoundingClientRect();
  const top = rect.top + (window.scrollY || 0);
  window.scrollTo({ top, behavior: reduced.value ? 'auto' : 'smooth' });

  const prevTabindex = target.getAttribute('tabindex');
  if (prevTabindex === null) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
  if (prevTabindex === null) {
    const cleanup = (): void => target.removeAttribute('tabindex');
    target.addEventListener('blur', cleanup, { once: true });
  }
}

const clickCardButton = (item: SolutionItem) => {
  const path = solutionTo(item);

  yaTrackEvent('NewSiteClickToSubpage', { type: item.id });

  if (path) router.push(path);
};

//const calculatorTo = computed(() => `/${locale.value}/calculator`);
</script>
